import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../api/endpoints/auth';
import { Button } from '../../components/common';
import { useDocumentTitle } from '../../hooks';
import './Auth.css';

interface EmailVerificationProps {
  email?: string;
  onSuccess?: () => void;
}

/**
 * EmailVerification Component
 * Handles email verification with 5-digit code
 * Implements Requirements 1.2
 */
export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email: propEmail,
  onSuccess
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = propEmail || (location.state as any)?.email || '';
  
  // Set document title
  useDocumentTitle('pageTitles.verifyEmail');
  
  const [code, setCode] = useState(['', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only accept 5 digits
    if (/^\d{5}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      // Focus last input
      inputRefs.current[4]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const verificationCode = code.join('');
    
    if (verificationCode.length !== 5) {
      setError(t('validation.enterCompleteCode'));
      return;
    }

    if (!email) {
      setError(t('validation.emailMissing'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authAPI.verifyEmail({ email, code: verificationCode });
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/login', { 
          state: { message: t('auth.emailVerifiedSuccess') }
        });
      }
    } catch (err: any) {
      setError(err.message || t('validation.invalidVerificationCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError(t('validation.emailMissing'));
      return;
    }

    setIsResending(true);
    setError('');

    try {
      // Note: Backend should have a resend endpoint
      // For now, we'll just show a success message
      // await authAPI.resendVerificationCode({ email });
      
      // Simulated success
      setTimeout(() => {
        setIsResending(false);
        setError('');
        // Show success message (in a real app, use toast)
        alert(t('auth.verificationCode') + ' resent to your email');
      }, 1000);
    } catch (err: any) {
      setError(err.message || t('errors.failedToSave'));
      setIsResending(false);
    }
  };

  return (
    <div className="auth-form">
      <h2 className="auth-form__title">{t('auth.verifyEmail')}</h2>
      
      <p className="auth-form__description">
        {t('auth.verifyEmailDescription')} <strong>{email}</strong>
      </p>

      {error && (
        <div className="auth-form__error" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="verification-code">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="verification-code__input"
              aria-label={t('auth.digitLabel', { number: index + 1 })}
              disabled={isLoading}
            />
          ))}
        </div>

        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading || code.some(d => !d)}
          fullWidth
        >
          {isLoading ? t('auth.verifying') : t('auth.verifyEmailButton')}
        </Button>
      </form>

      <div className="auth-form__footer">
        <p>
          {t('auth.didntReceiveCode')}{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="auth-form__link-button"
          >
            {isResending ? t('auth.resending') : t('auth.resend')}
          </button>
        </p>
      </div>
    </div>
  );
};
